import React, { useCallback, useMemo } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
// Common
import { PopupMenuItem } from '$common/popup-menu'
import { ReportIcon } from '$common/icons/report'
import { ShareIcon } from '$common/icons/share'
import { LogOutIcon } from '$common/icons'
// Logic
import { useRoom } from '$logic/room/react-hook'
import { useHall } from '$logic/hall/react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { MemberPermissions } from '$logic/types/subscription'
import { decodeSubscriptionPermissions } from '$logic/subscription'
// Localization
import { showLeaveHallModal } from '$comp/modals/confirmation-dialog'
import { useSubscription } from '$logic/subscription/react-hook'
import { t } from '$i18n'
import { showReportSendSnackbar } from '$comp/snackbars'
import { ContextMenuList } from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { shareLink } from '$func/share-api'
import { getHallPublicLink } from '$func/hall'

interface Props {
  hallId: string
  onClose: () => unknown
  xAxis: number
  yAxis: number
}

export function HallProfileMenu({ hallId, yAxis, onClose, xAxis }: Props) {
  const room = useRoom(hallId)
  const isGroup = room?.type === 'GROUP'
  const { value: permissions } = useAsyncFunction<MemberPermissions>(
    () => decodeSubscriptionPermissions(hallId),
    [hallId],
  )
  const isAdmin = permissions?.isAdmin
  const isOwner = permissions?.isOwner
  const { entity: subscription } = useSubscription(hallId)

  const { entity: hall } = useHall(hallId)
  const { public: isPublic, description } = hall || {}

  const hallPublicLink = getHallPublicLink(hall)

  const onReport = useCallback(async () => {
    await closeCurrentModal()
    showReportSendSnackbar()
    //   TODO
  }, [])

  const { shareable, onShare: onShareCb } = useMemo(
    () => shareLink(hallPublicLink, description),
    [hallPublicLink, description],
  )

  const onShare = useCallback(async () => {
    await closeCurrentModal()
    onShareCb()
  }, [onShareCb])
  const onLeave = useCallback(async () => {
    await closeCurrentModal()
    await showLeaveHallModal(hallId)
  }, [])

  return (
    <ContextMenuList onClose={onClose} open xAxis={xAxis} yAxis={yAxis}>
      {!isAdmin && (
        <PopupMenuItem text={t('menu.common.report')} onClick={onReport}>
          <ReportIcon />
        </PopupMenuItem>
      )}

      {isPublic && shareable && (
        <PopupMenuItem text={t('menu.common.share')} onClick={onShare}>
          <ShareIcon />
        </PopupMenuItem>
      )}

      {!isOwner && subscription && (
        <PopupMenuItem
          text={
            isGroup ? t('menu.hall.leaveGroup') : t('menu.hall.leaveChannel')
          }
          onClick={onLeave}
          error
        >
          <LogOutIcon sx={{ color: 'error.main' }} />
        </PopupMenuItem>
      )}
    </ContextMenuList>
  )
}
